import moment from "moment/moment";
import { fieldMapping } from "utils/constant";

function numberOrNull(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function formatPercent(value) {
  const numeric = numberOrNull(value);
  return numeric === null ? "-" : `${Math.round(numeric)}%`;
}

function formatVolume(value) {
  const numeric = numberOrNull(value);
  return numeric === null ? "-" : `${Math.round(numeric)} gal`;
}

function formatTemperature(value) {
  const numeric = numberOrNull(value);
  return numeric === null ? "" : `${Math.round(numeric)}F`;
}

function getEntityId(device) {
  return (
    device?.attributes?.entity_address_item?.entity_id ||
    device?.attributes?.entity_address_item?.Entity?.entity_id ||
    device?.entity_id ||
    null
  );
}

function getAlertInfo(alerts) {
  if (Array.isArray(alerts)) return alerts.length ? alerts[alerts.length - 1] : null;
  return alerts && Object.keys(alerts).length ? alerts : null;
}

function getChannelPercent(channel) {
  return channel?.current_value ?? channel?.percent_level ?? channel?.fill_percentage ?? null;
}

function getDeviceChannels(channels = {}) {
  return Object.values(channels)
    .filter(Boolean)
    .map((channel) => ({
      channelNumber: channel.channel_number,
      name: channel.channel_name || `Channel ${channel.channel_number}`,
      percent: getChannelPercent(channel),
      volume: channel.volume_level,
      threshold: channel.state?.range_max || null,
      rawLevel: channel.raw_level,
      actualFluidHeight: channel.actual_fluid_height,
      unit: channel.state?.unit || "%",
      displayLabel: channel.state?.display_label,
      displayColor: channel.state?.display_color,
    }));
}

function pickChannel(channels, patterns, fallbackIndex) {
  const found = channels.find((channel) =>
    patterns.some((pattern) => String(channel.name).toLowerCase().includes(pattern))
  );
  return found || channels[fallbackIndex] || null;
}

function buildTankValue(channel, threshold) {
  return {
    label: channel?.name || "-",
    percent: formatPercent(channel?.percent),
    numericPercent: numberOrNull(channel?.percent),
    volume: formatVolume(channel?.volume),
    threshold: channel?.threshold || threshold,
    channelNumber: channel?.channelNumber ?? 0
  };
}

function buildTankSlots(channels) {
  const fresh = pickChannel(channels, ["fresh"], 0);
  const wasteChannels = channels.filter((channel) => !String(channel.name).toLowerCase().includes("fresh"));
  return {
    fresh: buildTankValue(fresh, 25),
    // waste: buildTankValue(pickChannel(wasteChannels, ["waste", "holding", "tank"], 0), 75),
    waste: buildTankValue(pickChannel(wasteChannels, ["waste", "#1"], 0), 75),
    waste2: buildTankValue(pickChannel(wasteChannels, ["#1", "#2"], 1), 75),
    waste3: buildTankValue(pickChannel(wasteChannels, ["#2", "#3"], 2), 75),
  };
}

function buildTempValue(value) {
  return {
    label: value || "",
    value: formatTemperature(value),
    numericValue: numberOrNull(value),
    threshold: 40,
  };
}

function buildTempSlots(device_value) {
  // const tempChannels = channels.filter((channel) => String(channel.name).toLowerCase().includes("temp"));

  // return {
  //   menTemp: buildTempValue(pickChannel(tempChannels, ["men"], 0)),
  //   womenTemp: buildTempValue(pickChannel(tempChannels, ["women"], 1)),
  //   utilityTemp: buildTempValue(pickChannel(tempChannels, ["pt100_1"], 2)),
  // };

  return {
    menTemp: '',
    womenTemp: '',
    utilityTemp: buildTempValue(device_value?.raw_data?.pt100_1),
  };
}

function getDeviceText(attributes) {
  const { device_value, entity_address_item, device_data, device_analytics, children } = attributes;
  const device_type_id = device_data?.device_type_id;

  const mapId = entity_address_item?.map_identifier || '';

  if (device_type_id === 9 && children.length > 0) {
    const current_value = device_value?.channels?.[9]?.current_value
    const percent = current_value ?? Object.values(device_value?.channels || {}).reduce((sum, ch) => sum + (ch.percent_level ?? 0), 0);
    let mapIde = children[0]?.entity_address_item?.map_identifier
    return mapIde ? `${mapIde} ${percent}%` : `${percent}%`;
  }
  return mapId || `#${attributes.device_id}`;
}

function getDeviceLatLng(attributes) {
  const { device_value, entity_address_item, itemAttributeValues, device_data, children } = attributes;
  let lat = parseFloat(device_value?.position_latitude || entity_address_item?.latitude);
  let lng = parseFloat(device_value?.position_longitude || entity_address_item?.longitude);

  if ([4, 9].includes(device_data?.device_type_id)) {
    const deviceSetupFields = itemAttributeValues?.reduce((acc, item) => {
      const fieldName = fieldMapping[item.attribute_id];
      if (fieldName) acc[fieldName] = item.value;
      return acc;
    }, {});
    lat = parseFloat(deviceSetupFields?.latitude || entity_address_item?.latitude);
    lng = parseFloat(deviceSetupFields?.longitude || entity_address_item?.longitude);
  }

  if (children.length > 0 && [4, 9].includes(device_data?.device_type_id)) {
    const { position_latitude, position_longitude } = children[0].device_value
    lat = position_latitude ?? lat
    lng = position_longitude ?? lng
  }

  if (!lat || !lng) return null;
  return { lat, lng };
}

export function normalizeDashboardRows(devices = [], badgesList = []) {

  return devices.map((device, index) => {

    const attributes = device?.attributes || {};
    const entityItem = attributes?.entity_address_item || {};
    const channels = getDeviceChannels(attributes?.device_value?.channels || {});
    const alertInfo = getAlertInfo(attributes?.alerts);
    const tankSlots = buildTankSlots(channels);
    const tempSlots = buildTempSlots(attributes?.device_value);
    const lastUpdated = attributes?.device_value?.last_updated_on || attributes?.device_value?.last_updated || attributes?.device_value?.timestamp;
    const status = attributes?.device_value?.moment_status || attributes?.device_value?.communication_status || "Operational";
    // const power = attributes?.device_value?.external_powersource_status === false ? "Off" : "On";
    const power = attributes?.device_value?.raw_data?.gpio_input_1 === 'off' ? "Off" : "On";
    const child = attributes?.children?.length > 0 ? attributes?.children : [];
    const battery = child.length > 0 ? child[0]?.device_value?.battery_level : "";


    const latLng = getDeviceLatLng(device.attributes);
    if (!latLng) return null;

    const { lat, lng } = latLng;
    const { device_value, entity_address_item, device_data, device_id, children, alerts } = device?.attributes;
    const alert = alerts?.length ? alerts[alerts.length - 1] : null
    let color = 'green';
    let badgePick = 'green';
    if (alert) {
      let badge = badgesList.find((e) => e.badge_id === alert?.badge_id)
      if (badge) {
        color = badge?.font_color_hex ?? 'green'
        badgePick = badge;
      }
    }

    let location = attributes?.geofences?.name || attributes?.device_value?.reverse_geocoded_address?.formatted_address || [entityItem?.street_address1, entityItem?.city, entityItem?.state, entityItem?.postal_code].filter(Boolean).join(", ") || "-";
    if(children && children?.length > 0) {
      location = children[0]?.device_value?.reverse_geocoded_address?.formatted_address || location;
    }

    return {
      id: device.id || device.device_id || index + 1,
      name: attributes?.device_data?.name || `Device ${device.device_id || index + 1}`,
      identifier: attributes?.device_data?.external_device_id || attributes?.external_device_id || attributes?.device_value?.ident || "-",
      entityName: entityItem?.entity_name || [entityItem?.first_name, entityItem?.last_name].filter(Boolean).join(" ") || "-",
      itemName: entityItem?.entity_address_name || entityItem?.item_name || "-",
      location: location,
      status,
      power,
      alert_obj: alert,
      alert: alertInfo?.description || "-",
      alertType: alertInfo?.type_name || "-",
      alertAge: alertInfo?.created_at ? moment(alertInfo.created_at).fromNow() : "-",
      lastUpdated: lastUpdated ? moment(lastUpdated).fromNow() : "-",
      batteryVoltage: battery || "",
      batteryNumeric: numberOrNull(battery),
      entityId: getEntityId(device),
      channels,
      lat,
      lng,
      iconClassName: entity_address_item?.class_name,
      color: color,
      badgePick,
      animate: device_value?.moment_status === 'Moving',
      text: getDeviceText(device.attributes),
      title: `${device_data?.name ?? ''} #${device_id}`,
      heading: device_value?.position_direction,
      ignore_heading: entity_address_item?.ignore_heading,
      ...tankSlots,
      ...tempSlots,
    };
  });
}